import { useState, useContext } from "react";

import { putGroup } from "@rubi/api-generated";
import { toast } from "@rubi/ui";
import { useNavigate } from "react-router";

import { AuthStateContext } from "@/contexts/AuthContext";

import AlertGroupForm, { GroupFormData } from "./AlertGroupForm";

const AlertGroupCreate = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const { refreshUserGroups } = useContext(AuthStateContext);

  const handleSubmit = async (data: GroupFormData) => {
    setError("");

    if (!data.name || data.name.trim() === "") {
      setError("Please enter a group name");
      return;
    }

    if (data.isPublic) {
      const validContacts = data.publicContacts.filter(
        (contact) =>
          contact.name?.trim() ||
          contact.email?.trim() ||
          contact.phone_number?.trim(),
      );
      if (validContacts.length === 0) {
        setError(
          "At least one public contact is required when the group is public",
        );
        return;
      }
    }

    setLoading(true);

    try {
      const validContacts = data.isPublic
        ? data.publicContacts.filter(
            (contact) =>
              contact.name?.trim() ||
              contact.email?.trim() ||
              contact.phone_number?.trim(),
          )
        : [];

      const description = data.description.trim();
      const response = await putGroup({
        body: {
          name: data.name.trim(),
          ...(description ? { description } : {}),
          ...(data.isPublic
            ? {
                is_public: true,
                public_contact: validContacts as Array<Record<string, unknown>>,
              }
            : {}),
          ...(data.requiresAdmittanceModeration
            ? { requires_admittance_moderation: true }
            : {}),
        },
      });

      // Refresh user groups to update permissions
      await refreshUserGroups();

      toast.success("Group created successfully!");
      navigate(`/rubialert/manage-groups/${response.data?.id}/`);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        "An error occurred when creating the group. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/rubialert/manage-groups");
  };

  return (
    <AlertGroupForm
      mode="create"
      loading={loading}
      error={error}
      onSubmit={handleSubmit}
      onBack={handleBack}
      title="Create New Group"
      subtitle="Create a new alert group. You will be set as the owner of the group."
      submitButtonText="Create Group"
      submitButtonLoadingText="Creating..."
    />
  );
};

export default AlertGroupCreate;
